import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatMoney } from '@/lib/format';

interface Usage {
  id: string;
  code_id: string;
  sale_id: string | null;
  used_by: string | null;
  used_by_role: string | null;
  amount_discounted: number;
  created_at: string;
  code: { code: string; scope: string; percentage: number } | null;
  sale: { invoice_number: string } | null;
  profile: { first_name: string | null; last_name: string | null } | null;
}

export default function DiscountHistory() {
  const [items, setItems] = useState<Usage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('discount_code_usages')
        .select(
          '*, code:discount_codes(code, scope, percentage), sale:sales(invoice_number), profile:profiles!used_by(first_name, last_name)'
        )
        .order('created_at', { ascending: false })
        .limit(100);
      if (e) throw e;
      setItems((data as Usage[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const scopeLabel = (s: string) => (s === 'admin' ? 'Administrador' : 'Cajero');

  return (
    <div>
      <p className="text-sm text-foreground-600 mb-4">
        Registro de cada descuento aplicado con código (auditoría).
      </p>

      {error && (
        <div className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-history-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no se han usado códigos de descuento.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Código</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Factura</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Usuario</th>
                <th className="px-4 py-3 font-medium">Monto</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {items.map((u) => (
                <tr key={u.id} className="hover:bg-background-50">
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-foreground-950">
                      {u.code?.code ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.code?.scope === 'admin'
                          ? 'bg-secondary-100 text-secondary-900'
                          : 'bg-accent-100 text-accent-900'
                      }`}
                    >
                      {scopeLabel(u.code?.scope ?? '')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 hidden md:table-cell">
                    {u.sale?.invoice_number ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-700 hidden lg:table-cell">
                    {u.profile ? [u.profile.first_name, u.profile.last_name].filter(Boolean).join(' ') : '—'}
                  </td>
                  <td className="px-4 py-3 text-accent-700 font-semibold">
                    -{formatMoney(Number(u.amount_discounted), 'RD$')}
                  </td>
                  <td className="px-4 py-3 text-foreground-500 hidden lg:table-cell">
                    {new Date(u.created_at).toLocaleString('es-DO')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}